<script lang="ts">
  import type { Poll, PollState } from '@pin/schemas/poll';
  import { format } from 'date-fns';
  import {
    createPoll,
    deletePoll,
    editPoll,
    hidePollResult,
    pollSubject$,
    setActivePoll,
    setPollClosed,
    showPollResult,
  } from '../../api/polls.api';
  import { now } from '../../stores/now';
  import { addToast } from '../../stores/toastStore';
  import { formatElapsed } from '../../utils/elapsed';

  interface Props {
    polls: PollState | null;
  }
  let { polls }: Props = $props();
  let mode = $state<'list' | 'create' | 'edit'>('list');

  let editingId = $state<string | null>(null);
  let newQuestion = $state('');
  let newAlternatives = $state(['', '', '']);
  const maxAlternatives = 5;
  const minAlternatives = 1;

  const handleDeletePoll = async (poll: Poll) => {
    const { id, question } = poll;
    try {
      await deletePoll(id);
      pollSubject$.next();
      addToast({ message: `Poll '${question}' deleted` });
    } catch (e) {
      addToast({ message: `Couldn't delete poll '${question}': ${String(e)}` });
    }
  };

  const handleSetPollClosed = async (poll: Poll) => {
    const { question } = poll;
    try {
      await setPollClosed();
      pollSubject$.next();
      addToast({
        message: `Poll '${question}' set as inactive`,
      });
    } catch (e) {
      addToast({
        message: `Couldn't set poll '${question}' as inactive: ${String(e)}`,
        type: 'error',
      });
    }
  };

  const handleSetActivePoll = async (poll: Poll) => {
    const { question } = poll;
    try {
      await setActivePoll(poll);
      pollSubject$.next();
      addToast({ message: `Poll '${question}' set as active` });
    } catch (e) {
      addToast({
        message: `Couldn't set poll '${question}' as active: ${String(e)}`,
        type: 'error',
      });
    }
  };

  const handleRepublishPoll = async (poll: Poll) => {
    const { question } = poll;
    try {
      await setActivePoll(poll);
      pollSubject$.next();
      addToast({ message: `Poll '${question}' republished with its votes` });
    } catch (e) {
      addToast({
        message: `Couldn't republish poll '${question}': ${String(e)}`,
        type: 'error',
      });
    }
  };

  const handleShowResult = async (poll: Poll) => {
    const { id, question } = poll;
    try {
      await showPollResult(id);
      pollSubject$.next();
      addToast({ message: `Result of '${question}' shown in the overlay` });
    } catch (e) {
      addToast({
        message: `Couldn't show the result of '${question}': ${String(e)}`,
        type: 'error',
      });
    }
  };

  const handleHideResult = async (poll: Poll) => {
    const { question } = poll;
    try {
      await hidePollResult();
      pollSubject$.next();
      addToast({ message: `Result of '${question}' hidden from the overlay` });
    } catch (e) {
      addToast({
        message: `Couldn't hide the result of '${question}': ${String(e)}`,
        type: 'error',
      });
    }
  };

  const handlePollCreate = async () => {
    const alternatives = newAlternatives.filter((text) => text.trim());
    if (!newQuestion.trim()) {
      addToast({ message: 'Question cannot be empty', type: 'error' });
      return;
    }
    if (alternatives.length === 0) {
      addToast({
        message: 'Please add at least one nonempty alternative',
        type: 'error',
      });
      return;
    }
    try {
      await createPoll({ question: newQuestion, answers: alternatives });
      pollSubject$.next();
      addToast({ message: `Poll '${newQuestion}' created` });
      newQuestion = '';
      newAlternatives = ['', '', ''];
      mode = 'list';
    } catch (e) {
      addToast({
        message: `Couldn't create poll '${newQuestion}: ${String(e)}'`,
        type: 'error',
      });
    }
  };

  const handlePollEdit = async () => {
    const alternatives = newAlternatives.filter((text) => text.trim());
    if (editingId == null) {
      return;
    }
    if (!newQuestion.trim()) {
      addToast({ message: 'Question cannot be empty', type: 'error' });
      return;
    }
    if (alternatives.length === 0) {
      addToast({
        message: 'Please add at least one nonempty alternative',
        type: 'error',
      });
      return;
    }
    try {
      await editPoll(editingId, {
        question: newQuestion,
        answers: alternatives,
      });
      pollSubject$.next();
      addToast({ message: `Poll '${newQuestion}' edited` });
      editingId = null;
      newQuestion = '';
      newAlternatives = ['', '', ''];
      mode = 'list';
    } catch (e) {
      addToast({
        message: `Couldn't edit poll '${newQuestion}: ${String(e)}'`,
        type: 'error',
      });
    }
  };

  const handlePollDuplicate = async (poll: Poll) => {
    const { question, alternatives } = poll;
    const answers = alternatives.map(({ text }) => text);
    try {
      await createPoll({ question, answers });
      pollSubject$.next();
      addToast({ message: `Poll '${question}' duplicated` });
      mode = 'list';
    } catch (e) {
      addToast({
        message: `Couldn't duplicate poll '${question}: ${String(e)}'`,
        type: 'error',
      });
    }
  };
</script>

<div class="container">
  {#if mode === 'create' || mode === 'edit'}
    <form
      class="create-poll-container"
      onsubmit={(e) => {
        e.preventDefault();
        if (mode === 'create') {
          handlePollCreate();
        }
        if (mode === 'edit') {
          handlePollEdit();
        }
      }}
      onreset={(e) => {
        e.preventDefault();
        mode = 'list';
        newQuestion = '';
        newAlternatives = ['', '', ''];
        editingId = null;
      }}
    >
      <label>
        Question:
        <input bind:value={newQuestion} placeholder="Question..." />
      </label>
      {#each newAlternatives as _, i (i)}
        <label>
          Alternative {i + 1}:
          <input
            bind:value={newAlternatives[i]}
            placeholder="Alternative.. (leave empty to omit)"
          />
        </label>
      {/each}

      <div class="alt-controls">
        <button
          class="btn small"
          type="button"
          onclick={() => newAlternatives.push('')}
          disabled={newAlternatives.length >= maxAlternatives}
        >
          + Add
        </button>
        <button
          class="btn small"
          type="button"
          onclick={() => newAlternatives.splice(-1, 1)}
          disabled={newAlternatives.length <= minAlternatives}
        >
          − Remove
        </button>
        <span class="alt-count">{newAlternatives.length}/{maxAlternatives}</span
        >
      </div>

      <div class="create-actions">
        <button class="btn" type="submit"
          >{mode === 'edit' ? 'Update' : 'Create'}</button
        >
        <button class="btn" type="reset">Cancel</button>
      </div>
    </form>
  {:else}
    <div class="header">
      <button class="btn" onclick={() => (mode = 'create')}>+ New Poll</button>
    </div>
    <div class="polls">
      {#if polls === null}
        <p>Loading...</p>
      {:else}
        {#each polls.candidates as poll (poll.id)}
          {@const active = poll.id === polls.live?.id}
          {@const resultShown = poll.id === polls.overlayResultId}
          {@const hasVotes = poll.alternatives.some(({ votes }) => votes !== 0)}
          <div class="poll">
            {#if active}
              <span class="active-tag"></span>
            {/if}
            <div class="row-actions">
              {#if active}
                <button
                  class="btn small"
                  onclick={() => handleSetPollClosed(poll)}>Unpublish</button
                >
              {:else if hasVotes}
                {@const votersExpired = polls.expiredVotersIds?.includes(
                  poll.id
                )}
                <button
                  class="btn small republish"
                  onclick={() => handleRepublishPoll(poll)}
                  disabled={votersExpired}
                  title={votersExpired
                    ? 'Who voted is forgotten 90 days after the last vote, so everyone could vote again. Duplicate it to run it again.'
                    : 'Open the poll again with the votes it has. Those who have voted cannot vote again. Duplicate it to start from zero.'}
                  >Republish</button
                >
              {:else}
                <button
                  class="btn small"
                  onclick={() => handleSetActivePoll(poll)}>Set active</button
                >
              {/if}
              <button
                class="btn small overlay"
                class:on-air={resultShown}
                onclick={() =>
                  resultShown ? handleHideResult(poll) : handleShowResult(poll)}
                title={resultShown
                  ? 'Hide the result from the overlay'
                  : 'Show the result in the overlay. Replaces the result on air, if any.'}
                >{resultShown ? 'Hide' : 'Show'}</button
              >
            </div>
            <details>
              <summary>
                <strong>{poll.question}</strong>
                {#if active && polls.liveAtTs != null}
                  <span
                    class="timer"
                    title="Live in the chat since {format(
                      polls.liveAtTs,
                      'HH:mm:ss'
                    )}">{formatElapsed($now - polls.liveAtTs)}</span
                  >
                {/if}
              </summary>
              <ul>
                {#each poll.alternatives as alt, i (i)}
                  <li>
                    {alt.text}: {alt.votes} ({alt.percentage}%)
                  </li>
                {/each}
              </ul>
              <div class="poll-actions">
                <button class="btn" onclick={() => handlePollDuplicate(poll)}
                  >Duplicate</button
                >
                <button
                  class="btn"
                  onclick={() => {
                    editingId = poll.id;
                    newQuestion = poll.question;
                    newAlternatives = poll.alternatives.map(({ text }) => text);
                    mode = 'edit';
                  }}>Edit</button
                >
                <button class="btn" onclick={() => handleDeletePoll(poll)}
                  >Delete</button
                >
              </div>
            </details>
          </div>
        {/each}
        {#if polls.candidates.length === 0}
          <p>No polls yet</p>
        {/if}
      {/if}
    </div>
  {/if}
</div>

<style>
  .container {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    font-size: 0.95rem;
  }

  .create-poll-container {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin: 0;
    padding: 0;
    margin-top: 0.25rem;
  }

  .header {
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }

  .polls {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  details {
    background: var(--color-zinc-800);
    border-radius: 0.5rem;
    padding: 0.5rem 1rem;
  }

  summary {
    display: flex;
    font-weight: 600;
    cursor: pointer;
    user-select: none;
    padding-right: 10.5rem;
  }

  .timer {
    margin-left: auto;
    padding-left: 0.5rem;
    font-weight: 400;
    font-size: 0.85rem;
    color: var(--color-zinc-300);
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }

  .poll {
    position: relative;
  }

  .active-tag {
    background: var(--color-red-500);
    height: 10px;
    width: 10px;
    border-radius: 9999px;
    position: absolute;
    left: 0.3rem;
    top: calc(0.5rem + 0.5lh - 5px);
  }

  .row-actions {
    position: absolute;
    right: 0.5rem;
    top: 0.5rem;
    display: flex;
    gap: 0.25rem;
  }

  .btn.overlay {
    background-color: var(--color-green-800);
    border-color: var(--color-green-700);
  }

  .btn.overlay:hover {
    background-color: var(--color-green-700);
  }

  .btn.overlay.on-air {
    background-color: var(--color-red-800);
    border-color: var(--color-red-700);
  }

  .btn.overlay.on-air:hover {
    background-color: var(--color-red-700);
  }

  .btn.republish {
    background-color: var(--color-amber-800);
    border-color: var(--color-amber-700);
  }

  /* Darker, not lighter: the light text on amber-700 is below 4.5:1 */
  .btn.republish:hover {
    background-color: var(--color-amber-900);
  }

  .poll-actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 0.5rem;
    margin: 0.5rem 0;
  }

  ul {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    list-style: none;
    padding-left: 0;
    margin: 0;
  }

  label {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    font-weight: 500;
  }

  input {
    padding: 0.4rem;
    border: 1px solid var(--color-zinc-400);
    border-radius: 0.25rem;
    background-color: var(--color-zinc-200);
    color: var(--color-zinc-950);
  }

  .create-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
  }

  .alt-controls {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    margin-top: 0.5rem;
  }

  .alt-count {
    color: var(--color-zinc-400);
    font-size: 0.85rem;
  }

  .btn {
    padding: 0.25rem 0.75rem;
    background-color: var(--color-zinc-700);
    border-radius: 0.5rem;
    color: var(--color-zinc-200);
    border: 1px solid var(--color-zinc-600);
    font-size: 1rem;
    cursor: pointer;
  }

  .btn.small {
    padding: 0.25rem 0.6rem;
    font-size: 0.9rem;
  }

  .btn:hover {
    background-color: var(--color-zinc-600);
  }

  .btn:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
</style>
